import { StrictMode, Suspense, lazy } from 'react';
import { setupReactErrorHandler } from '@posthog/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { type RootOptions, createRoot } from 'react-dom/client';

import '@/api/client-init';
import { Toaster } from '@/components/ui/sonner';
// Side-effect import that reads window.location.search must run before PostHog rewrites history.
import '@/lib/ours';

import './index.css';

import '@/lib/analytics';

import { PosthogProvider, isPostHogEnabled, posthog } from './posthog';
import { queryClient } from './queryClient';
import { router } from './router/router';

const DevMockMenu =
  import.meta.env.VITE_ENABLE_MOCKS === 'true'
    ? lazy(() =>
        import('@/components/DevMockMenu').then((module) => ({ default: module.DevMockMenu }))
      )
    : null;

async function enableMocking() {
  if (import.meta.env.VITE_ENABLE_MOCKS !== 'true') return;

  const [{ worker }, { getActiveHandlers }] = await Promise.all([
    import('./mocks/browser'),
    import('./mocks/handlers'),
  ]);

  await worker.start({
    onUnhandledRequest: 'bypass',
  });
  worker.use(...getActiveHandlers());
}

/**
 * Only attach PostHog error handlers when PostHog is enabled.
 * These handlers capture React errors that occur outside error boundaries.
 */
function getCreateRootOptions(): RootOptions {
  if (!isPostHogEnabled) {
    return {};
  }

  return {
    onUncaughtError: setupReactErrorHandler(posthog, (error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    onCaughtError: setupReactErrorHandler(posthog, (error, errorInfo) => {
      console.warn('Caught error', error, errorInfo.componentStack);
    }),
    onRecoverableError: setupReactErrorHandler(posthog, (error, errorInfo) => {
      console.warn('Recoverable error', error, errorInfo.componentStack);
    }),
  };
}

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root element not found');
}

enableMocking().then(() => {
  const root = createRoot(container, getCreateRootOptions());

  root.render(
    <StrictMode>
      <PosthogProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          {DevMockMenu ? (
            <Suspense fallback={null}>
              <DevMockMenu />
            </Suspense>
          ) : null}
          <Toaster />
        </QueryClientProvider>
      </PosthogProvider>
    </StrictMode>
  );
});
